import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { PHOTO, LIFESTYLE_GALLERY } from "@/lib/content";
import { SplitText, Magnetic } from "@/components/fx/Motion";
import { useGirlPhoto } from "@/lib/holiday";

const EASE = [0.22, 1, 0.36, 1];

const HeroImages = ({ y, y2 }) => {
  const girl = useGirlPhoto();
  return (
  <div className="relative h-[62vh] sm:h-[70vh] lg:h-[82vh]">
    <motion.div className="absolute inset-0 lg:left-[8%] overflow-hidden" initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.5, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}>
      <motion.img data-testid="hero-image" src={girl} alt="Primus Shimmering Body Oil" style={{ y }} className="h-[115%] w-full object-cover object-[50%_30%]" />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0b0a08] to-transparent lg:hidden" />
    </motion.div>
    <motion.div style={{ y: y2 }} initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.2, delay: 1, ease: EASE }}
      className="hidden lg:block absolute -left-10 bottom-12 w-[38%] aspect-[3/4] overflow-hidden border-4 border-[#0b0a08] shadow-2xl">
      <img src={PHOTO.hand} alt="Primus Shimmering Body Oil" className="h-full w-full object-cover object-[55%_60%]" />
    </motion.div>
    <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.6, type: "spring", stiffness: 120 }}
      className="absolute top-6 right-6 h-24 w-24 rounded-full bg-gold text-[#0b0a08] flex items-center justify-center text-center font-display text-sm leading-tight p-3 rotate-[-8deg]">
      Shimmering Body Oil
    </motion.div>
  </div>
  );
};

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative grain overflow-hidden">
      <motion.div className="absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full bg-[#c9a55c]/15 blur-[120px]" animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0.9, 0.5] }} transition={{ duration: 8, repeat: Infinity }} />
      <div className="relative max-w-7xl mx-auto lg:px-10 grid lg:grid-cols-[1fr_1.05fr] gap-0 lg:gap-12 items-center lg:py-10">
        <div className="order-1 lg:order-2"><HeroImages y={y} y2={y2} /></div>
        <motion.div style={{ y: textY }} className="order-2 lg:order-1 px-5 lg:px-0 pb-16 lg:pb-0 -mt-16 lg:mt-0 relative">
          <motion.p className="eyebrow mb-6" initial={{ opacity: 0, letterSpacing: "0.6em" }} animate={{ opacity: 1, letterSpacing: "0.18em" }} transition={{ duration: 1.6, ease: EASE, delay: 0.3 }}>{t("hero_eyebrow")}</motion.p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.98]">
            <SplitText text={t("hero_title_1")} delay={0.4} stagger={0.09} /><br />
            <em className="text-gold"><SplitText text={t("hero_title_2")} delay={0.65} /></em> <SplitText text={t("hero_title_3")} delay={0.8} />
          </h1>
          <motion.p className="text-white/70 max-w-md mt-6 text-base" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.1 }}>{t("hero_text")}</motion.p>
          <motion.div className="flex flex-col sm:flex-row gap-3 mt-10" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.3, ease: EASE }}>
            <Magnetic><Link data-testid="hero-shop-now-button" to="/shop" className="btn-gold btn-shine h-14 px-10 flex items-center justify-center gap-3">{t("hero_cta")} <ArrowRight size={16} /></Link></Magnetic>
            <Magnetic><Link data-testid="hero-primus-button" to="/products/shimmering-body-oil-primus" className="btn-ghost h-14 px-10 flex items-center justify-center">Shimmering Body Oil</Link></Magnetic>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export const Lifestyle = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-4, 2]);
  const girl = useGirlPhoto();
  const gallery = LIFESTYLE_GALLERY.map((g) => (g.src === PHOTO.girl ? { ...g, src: girl } : g));
  const items = [...gallery, ...gallery, ...gallery, ...gallery];
  return (
    <section ref={ref} data-testid="lifestyle-section" className="py-16 lg:py-24 overflow-hidden">
      <motion.div style={{ rotate }} className="scale-[1.08]">
        <div className="marquee-photos flex gap-5 w-max">
          {items.map((g, i) => (
            <Link key={i} to={`/products/${g.handle}`} data-testid={`lifestyle-photo-${i}`} aria-label={g.name}
              className={`group relative shrink-0 overflow-hidden ${i % 2 ? "w-[52vw] sm:w-[30vw] lg:w-[19vw] translate-y-10" : "w-[60vw] sm:w-[34vw] lg:w-[23vw]"} aspect-[3/4]`}>
              <img src={g.src} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#c9a55c]/0 group-hover:ring-[#c9a55c]/70 transition-[box-shadow] duration-500" />
            </Link>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
